import { ChangeEvent, FormEvent, useEffect, useState } from "react";

import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Input from "#/components/shared/input";
import { Modal } from "#/components/shared/modal/modal";
import ThemedPhoneInput from "#/components/shared/themed-phone-input";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

const initialState = {
  phone: "",
  fax: "",
  email: "",
  providerName: "",
  institutionName: "",
  department: "",
};

export function ContactDetailEditModal() {
  const {
    isEditContactDetailOpen,
    closeEditContactDetail,
    contactToEdit,
    handleSaveExistingContact,
    isUpdatingContactDetail,
  } = useMedicalRecordRequestDetailContext();
  const [form, setForm] = useState(initialState);

  useEffect(() => {
    if (isEditContactDetailOpen) {
      setForm({
        phone: contactToEdit?.phone ?? "",
        fax: contactToEdit?.fax ?? "",
        email: contactToEdit?.email ?? "",
        providerName: "",
        institutionName: "",
        department: contactToEdit?.department ?? "",
      });
    }
  }, [isEditContactDetailOpen, contactToEdit]);

  const handleChange =
    (field: keyof typeof form) => (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleFaxChange = (value: string) => {
    setForm((prev) => ({ ...prev, fax: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await handleSaveExistingContact({
        phone: form.phone || null,
        fax: form.fax || null,
        email: form.email || null,
        providerName: form.providerName || null,
        institutionName: form.institutionName || null,
        department: form.department || null,
      });
    } catch (err) {
      console.error("Unable to save contact detail", err);
    }
  };

  const handleClose = () => {
    setForm(initialState);
    closeEditContactDetail();
  };

  return (
    <Modal
      isOpen={isEditContactDetailOpen}
      onClose={handleClose}
      title="Edit contact details"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ThemedPhoneInput
          label="Phone"
          value={form.phone}
          onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
          fullWidth
        />
        <ThemedPhoneInput
          label="Fax"
          value={form.fax}
          onChange={handleFaxChange}
          fullWidth
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={handleChange("email")}
          fullWidth
        />
        <Input
          label="Provider name (optional)"
          value={form.providerName}
          onChange={handleChange("providerName" as any)}
          fullWidth
          placeholder="Update provider display name"
        />
        <Input
          label="Institution name (optional)"
          value={form.institutionName}
          onChange={handleChange("institutionName" as any)}
          fullWidth
          placeholder="Update institution display name"
        />
        <Input
          label="Department (optional)"
          value={form.department}
          onChange={handleChange("department" as any)}
          fullWidth
          placeholder="Update department label"
        />
        <div className="flex justify-end gap-2">
          <SubmitButton
            type="button"
            color="beige"
            onClick={handleClose}
            hideArrow
          >
            Cancel
          </SubmitButton>
          <SubmitButton
            type="submit"
            isLoading={isUpdatingContactDetail}
            hideArrow
          >
            Save changes
          </SubmitButton>
        </div>
      </form>
    </Modal>
  );
}

export default ContactDetailEditModal;
